import { Component } from '@theme/component';
import { EngravingGalleryPromoteEvent, ThemeEvents } from '@theme/events';

const ENGRAVING_PROPERTY_NAME = 'properties[Engraving]';

/**
 * Manages the complimentary engraving line item option: character count, max length,
 * gallery promotion, and omitting the line item property when the field is empty.
 */
class ComplimentaryEngravingComponent extends Component {
  requiredRefs = ['textInput', 'characterCount'];

  #maxLength = 10;

  /** @type {HTMLFormElement | null} */
  #form = null;

  /**
   * @returns {HTMLInputElement | null}
   */
  #getTextInput() {
    const el = this.refs.textInput;
    return el instanceof HTMLInputElement ? el : null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getCharacterCount() {
    const el = this.refs.characterCount;
    return el instanceof HTMLElement && !Array.isArray(el) ? el : null;
  }

  connectedCallback() {
    super.connectedCallback();
    const textInput = this.#getTextInput();
    if (!textInput) {
      return;
    }
    this.#maxLength = Number(textInput.getAttribute('maxlength')) || 10;
    this.#form = this.closest('form');
    this.#form?.addEventListener('submit', this.#onFormSubmit, { capture: true });
    this.closest('.shopify-section')?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#updateCharacterCount();
    this.#syncGalleryPromotion();
  }

  disconnectedCallback() {
    this.closest('.shopify-section')?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#form?.removeEventListener('submit', this.#onFormSubmit, { capture: true });
    this.#dispatchGalleryPromote(false);
    super.disconnectedCallback();
  }

  #onVariantUpdate = () => {
    queueMicrotask(() => this.#syncGalleryPromotion());
  };

  /** @returns {boolean} */
  #isGalleryPromotionActive() {
    const textInput = this.#getTextInput();
    if (!textInput) return false;
    return textInput.value.trim().length > 0;
  }

  #syncGalleryPromotion() {
    this.#dispatchGalleryPromote(this.#isGalleryPromotionActive());
  }

  /**
   * @param {boolean} active
   */
  #dispatchGalleryPromote(active) {
    if (!this.dataset.galleryEngravingEnabled) return;
    this.dispatchEvent(new EngravingGalleryPromoteEvent(active));
  }

  /** @param {SubmitEvent} _event */
  #onFormSubmit = (_event) => {
    const textInput = this.#getTextInput();
    if (!textInput) {
      return;
    }
    if (!textInput.value.trim()) {
      textInput.removeAttribute('name');
    } else {
      textInput.setAttribute('name', ENGRAVING_PROPERTY_NAME);
    }
  };

  /**
   * @param {Event} _event
   */
  handleInput(_event) {
    const textInput = this.#getTextInput();
    if (!textInput) {
      return;
    }
    const validationError = this.refs.validationError;
    if (textInput.value.trim()) {
      textInput.setAttribute('name', ENGRAVING_PROPERTY_NAME);
    } else {
      textInput.removeAttribute('name');
    }
    if (textInput.value.length > this.#maxLength) {
      textInput.value = textInput.value.slice(0, this.#maxLength);
      if (validationError instanceof HTMLElement) {
        validationError.classList.remove('hidden');
      }
    } else {
      if (validationError instanceof HTMLElement) {
        validationError.classList.add('hidden');
      }
    }
    this.#updateCharacterCount();
    this.#syncGalleryPromotion();
  }

  #updateCharacterCount() {
    const textInput = this.#getTextInput();
    const characterCount = this.#getCharacterCount();
    if (!textInput || !characterCount) {
      return;
    }
    const currentLength = textInput.value.length;
    const maxLength = this.#maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', String(currentLength)).replace('[max]', String(maxLength));

    characterCount.textContent = updatedText;
  }
}

if (!customElements.get('complimentary-engraving-component')) {
  customElements.define('complimentary-engraving-component', ComplimentaryEngravingComponent);
}
